import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { IconLinkOutline18 as Link } from "nucleo-ui-outline-18"
import { z } from "zod"

import type { Workspace } from "@productlane/zero"
import {
	REVENUE_CURRENCY_CODES,
	REVENUE_CURRENCY_OPTIONS,
} from "@productlane/lib/src/currency"
import {
	AlertDialog,
	Avatar,
	Badge,
	Button,
	Form,
	FormSelect,
	FormSwitch,
	IntegrationTemplate,
	LinearIcon,
	useZodForm,
} from "@productlane/ui"

import type { RouterOutputs } from "@/lib/api"
import { MetaInfo } from "@/components/meta-info"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { env } from "@/env"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { hasLinearConnection } from "@/lib/linear-connection"
import { useSession } from "@/lib/session"
import { useAutoSave } from "@/lib/use-auto-save"
import { useZeroWorkspace } from "@/lib/zero"
import { resolveDefaultTeamId } from "@/screens/app/threads/hooks/resolve-default-team-id"
import { HowItWorksButton } from "../settings-switches/how-it-works-button"
import { SettingItem } from "../settings-switches/setting-item"
import { SettingsContainer } from "../settings-switches/settings-container"
import { IntegrationLayout } from "./integration-layout"
import { TeamTable } from "./linear-team-table"

const RevenueDisplayMode = ["ANNUAL", "MONTHLY"] as const

const GeneralFormSchema = z.object({
	defaultTeam: z.string(),
	selectedTeamIds: z.record(z.string(), z.boolean()),
	linearSyncTeamIds: z.record(z.string(), z.boolean()),
	revenueFormatting: z.enum(RevenueDisplayMode),
	revenueCurrency: z.enum(REVENUE_CURRENCY_CODES as [string, ...string[]]),
	autoCreateIssuesEnabled: z.boolean(),
	autoAssignFromLinearOwner: z.boolean(),
	autoCreateLinearCustomers: z.boolean(),
	postLinearResourceComment: z.boolean(),
	linkSuggestionsEnabled: z.boolean(),
})

const revenueFormattingOptions = [
	{ value: "MONTHLY", label: "Monthly" },
	{ value: "ANNUAL", label: "Annual" },
]

const LINEAR_TITLE = "Linear"
const LINEAR_DESCRIPTION = "Sync Linear projects, issues and customers."

const LINEAR_LONG_DESCRIPTION = (
	<div>
		<div className="mb-8">
			<div className="mb-2 text-lg font-semibold text-primary">
				How the Linear Integration Works
			</div>
			<div className="mb-6 text-sm leading-relaxed text-secondary">
				The Linear integration provides a powerful 2-way sync between
				Productlane and Linear, connecting your customer support directly to
				your development workflow. Every customer conversation, feature request,
				and bug report in Productlane can be linked to Linear issues, creating a
				seamless bridge between your support and engineering teams.
			</div>
		</div>

		<div className="mb-8">
			<div className="mb-2 text-base font-semibold text-primary">
				What Gets Synced
			</div>
			<div className="mb-4 space-y-2 text-sm text-secondary">
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Issues:</strong> Linear issues
						appear in Productlane as projects, maintaining status, priority, and
						assignees.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Teams:</strong> Linear teams sync
						to Productlane, allowing you to organize projects by team.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Status Updates:</strong> Issue
						updates in Linear reflect in Productlane in real-time.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Customer Links:</strong> Feedback
						and customer requests are linked to corresponding Linear issues.
					</span>
				</div>
			</div>
		</div>

		<div className="mb-8">
			<div className="mb-2 text-base font-semibold text-primary">
				2-Way Sync Benefits
			</div>
			<div className="mb-4 space-y-2 text-sm text-secondary">
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Create from Productlane:</strong>{" "}
						Create Linear issues from support requests without leaving
						Productlane.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Link Existing Issues:</strong>{" "}
						Connect customer conversations to Linear issues to track affected
						customers.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Automatic Updates:</strong> When an
						issue is completed in Linear, linked customers can be notified.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Context for Engineers:</strong>{" "}
						Engineers see customer support context and revenue impact in Linear
						issues.
					</span>
				</div>
			</div>
		</div>

		<div className="mb-8">
			<div className="mb-2 text-base font-semibold text-primary">
				Configuration Options
			</div>
			<div className="mb-4 text-sm leading-relaxed text-secondary">
				After connecting, you&apos;ll be able to configure which Linear teams
				are visible in Productlane, set a default team for new issues, and
				customize how customer revenue data is displayed alongside issues. This
				ensures your team only sees the relevant projects and information.
			</div>
		</div>

		<div className="mb-6">
			<div className="mb-2 text-base font-semibold text-primary">
				Getting Started
			</div>
			<div className="text-sm leading-relaxed text-secondary">
				Click &quot;Connect&quot; to authorize Productlane to access your Linear
				workspace. You&apos;ll be redirected to Linear to grant permissions.
				Once connected, all your teams and issues will automatically sync, and
				you can start linking customer support to your development workflow.
			</div>
		</div>
	</div>
)

export function LinearIntegrationPage() {
	const [isLoading, setIsLoading] = useState(false)
	const [dialogOpen, setDialogOpen] = useState(false)
	const { session } = useSession()
	const ctx = api.useUtils()
	const teamsQuery = api.linear.teams.useQuery(undefined, {
		cacheTime: Infinity,
		staleTime: Infinity,
	})
	const { mutateAsync, isLoading: isDisconnectLoading } =
		api.linear.disconnect.useMutation({
			onSuccess: async () => {
				await ctx.workspace.info.invalidate()
			},
		})

	const linearQuery = api.linear.isLinearConnected.useQuery(undefined, {
		cacheTime: Infinity,
		staleTime: Infinity,
	})
	const shareableConnectionsQuery = api.linear.shareableConnections.useQuery()
	const shareConnectionMutation = api.linear.shareConnection.useMutation({
		onSuccess: async () => {
			await ctx.workspace.info.invalidate()
			await linearQuery.refetch()
			await teamsQuery.refetch()
			toast.success({ title: "Linear connected from existing workspace" })
		},
	})
	const navigate = useNavigate()

	const workspace = useZeroWorkspace()
	// Not a Zero column: the workspaces table already sits at the TypeScript
	// inference ceiling for the client schema's permissions config.
	const workspaceInfo = api.workspace.info.useQuery()

	const disconnectLinear = async () => {
		analytics.track("disconnect-linear")
		await mutateAsync()
		await teamsQuery.refetch()
		await linearQuery.refetch()
		toast.success({ title: "Linear disconnected" })
		setDialogOpen(false)
	}

	if (!workspace) return null

	const shareableConnections = shareableConnectionsQuery.data ?? []

	return (
		<>
			<MetaInfo title="Linear Integration" />
			<IntegrationLayout />
			<IntegrationTemplate
				logo={<LinearIcon className="w-10 translate-x-px" />}
				name={
					<div className="flex flex-wrap items-center gap-2">
						{LINEAR_TITLE}{" "}
						{workspace?.linearOrganizationId ? (
							<Badge
								variant="green"
								size="sm"
								className="!text-green-700 dark:!text-green-400"
							>
								<span className="size-1.5 rounded-full bg-green-500" />
								Connected
							</Badge>
						) : null}
					</div>
				}
				description={LINEAR_DESCRIPTION}
				instructions={
					teamsQuery.data &&
					workspaceInfo.data &&
					hasLinearConnection(workspace) ? (
						<div>
							<LinearForm
								data={{
									workspace,
									teams: teamsQuery.data,
									autoCreateLinearCustomers:
										workspaceInfo.data.autoCreateLinearCustomers,
								}}
							/>
						</div>
					) : shareableConnections.length > 0 && !linearQuery.data ? (
						<div>
							<div className="mb-1 text-lg font-semibold text-primary">
								Use an existing connection
							</div>
							<div className="mb-4 text-sm text-secondary">
								Linear is already connected in another workspace you belong to.
								You can use the same connection.
							</div>
							<div className="space-y-2">
								{shareableConnections.map((connection) => (
									<div
										key={connection.workspaceId}
										className="border-border bg-card hover:bg-accent flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors"
									>
										{connection.workspaceLogoUrl ? (
											<Avatar
												name={workspace.name}
												image={connection.workspaceLogoUrl}
												className="size-8"
												roundness="md"
											/>
										) : (
											<div className="bg-muted flex size-8 items-center justify-center rounded-md">
												<Link className="text-muted-foreground size-4" />
											</div>
										)}
										<div className="min-w-0 flex-1">
											<div className="truncate text-sm font-medium text-primary">
												{connection.workspaceName}
											</div>
										</div>
										<Button
											size="sm"
											isLoading={shareConnectionMutation.isLoading}
											onClick={async () => {
												analytics.track("share-linear-connection")
												await shareConnectionMutation.mutateAsync({
													sourceWorkspaceId: connection.workspaceId,
												})
											}}
										>
											Use connection
										</Button>
									</div>
								))}
							</div>
						</div>
					) : (
						LINEAR_LONG_DESCRIPTION
					)
				}
				button={
					workspace && linearQuery.data ? (
						<div className="flex flex-row items-center gap-2">
							<HowItWorksButton
								article="680e2531-3bdd-4449-a0d7-622ee1aed10a"
								variant="icon"
							/>
							<Button
								variant="outline"
								onClick={() => {
									if (session?.membershipRole === "ADMIN") {
										setDialogOpen(true)
										return
									}
									toast.info({
										title: "Action not allowed",
										description:
											"Only admins have permission to disconnect integrations.",
										action: {
											label: "View your role",
											callback: () => {
												void navigate({ to: "/settings/members" })
											},
										},
									})
								}}
								disabled={session?.membershipRole !== "ADMIN"}
								isLoading={isDisconnectLoading}
							>
								Disconnect
							</Button>
						</div>
					) : (
						<div className="flex flex-row items-center gap-2">
							<HowItWorksButton
								article="680e2531-3bdd-4449-a0d7-622ee1aed10a"
								variant="icon"
							/>
							<RoleCheck not={["VIEWER"]}>
								<Button
									isLoading={isLoading}
									onClick={() => {
										setIsLoading(true)
										analytics.track("connect-linear")
										window.location.href = `${env.VITE_API_URL}/api/auth/linear?action=connect&prompt=consent`
									}}
								>
									Connect
								</Button>
							</RoleCheck>
						</div>
					)
				}
			/>

			<AlertDialog
				variant="positive"
				isOpen={dialogOpen}
				onOpenChange={setDialogOpen}
				confirmText="Disconnect"
				title="Disconnect Linear Integration?"
				description="Your data will remain saved, but the integration will be disabled."
				onCancel={() => {
					setDialogOpen(false)
				}}
				onConfirm={disconnectLinear}
			/>
		</>
	)
}

interface LinearFormProps {
	data: {
		teams: RouterOutputs["linear"]["teams"]
		workspace: Workspace
		autoCreateLinearCustomers: boolean
	}
}

export function LinearForm({ data }: LinearFormProps) {
	const form = useZodForm({
		schema: GeneralFormSchema,
		defaultValues: {
			defaultTeam:
				resolveDefaultTeamId(
					data.workspace.defaultLinearTeamId,
					(data.teams ?? []).filter(({ id }) =>
						data.workspace.selectedTeamIds?.includes(id),
					),
				) ?? "",
			selectedTeamIds: data.teams
				? data.teams.reduce(
						(prev, curr) => {
							return {
								...prev,
								[curr.id]:
									data.workspace.selectedTeamIds?.includes(curr.id) ?? false,
							}
						},
						{} as Record<string, boolean>,
					)
				: {},
			linearSyncTeamIds: Object.fromEntries(
				(data.teams ?? []).map((team) => [
					team.id,
					data.workspace.linearSyncTeamIds?.includes(team.id) ?? false,
				]),
			),
			revenueFormatting: data.workspace.revenueFormatting,
			revenueCurrency: data.workspace.revenueCurrency,
			autoCreateIssuesEnabled: data.workspace.autoCreateLinearIssues ?? false,
			autoAssignFromLinearOwner:
				data.workspace.autoAssignFromLinearOwner ?? false,
			autoCreateLinearCustomers: data.autoCreateLinearCustomers,
			postLinearResourceComment:
				data.workspace.postLinearResourceComment ?? false,
			linkSuggestionsEnabled: data.workspace.linkSuggestionsEnabled ?? true,
		},
		mode: "onChange",
	})

	const ctx = api.useUtils()
	const updateMutation = api.workspace.update.useMutation({
		onSuccess: async () => {
			await ctx.workspace.info.invalidate()
		},
	})

	useAutoSave({
		form,
		action: "autosave linear settings",
		onSave: async (values) => {
			const defaultTeamId = resolveDefaultTeamId(
				values.defaultTeam,
				(data.teams || []).filter(({ id }) => values.selectedTeamIds[id]),
			)
			if (defaultTeamId && defaultTeamId !== values.defaultTeam) {
				form.setValue("defaultTeam", defaultTeamId)
			}
			await updateMutation.mutateAsync({
				defaultTeamId,
				selectedTeamIds: values.selectedTeamIds,
				linearSyncTeamIds: values.linearSyncTeamIds,
				revenueFormatting: values.revenueFormatting,
				revenueCurrency: values.revenueCurrency,
				autoCreateLinearIssues: values.autoCreateIssuesEnabled,
				autoAssignFromLinearOwner: values.autoAssignFromLinearOwner,
				autoCreateLinearCustomers: values.autoCreateLinearCustomers,
				postLinearResourceComment: values.postLinearResourceComment,
				linkSuggestionsEnabled: values.linkSuggestionsEnabled,
			})
			analytics.track("update-linear-settings")
		},
	})

	const selectedTeamIds = form.watch("selectedTeamIds")
	const linearSyncTeamIds = form.watch("linearSyncTeamIds")

	if (!data.teams) {
		return null
	}

	return (
		<Form
			className="space-y-4"
			form={form}
			onSubmit={async (values) => {
				await updateMutation.mutateAsync({
					defaultTeamId: values.defaultTeam || undefined,
					selectedTeamIds: values.selectedTeamIds,
					linearSyncTeamIds: values.linearSyncTeamIds,
					revenueFormatting: values.revenueFormatting,
					revenueCurrency: values.revenueCurrency,
					autoCreateLinearIssues: values.autoCreateIssuesEnabled,
					autoCreateLinearCustomers: values.autoCreateLinearCustomers,
					postLinearResourceComment: values.postLinearResourceComment,
				})
				analytics.track("update-linear-settings")
			}}
		>
			<div className="space-y-4">
				<div className="text-sm font-medium">Teams</div>
				<SettingsContainer>
					<TeamTable
						teams={data.teams}
						noneSynced={!Object.values(linearSyncTeamIds ?? {}).some(Boolean)}
					/>
					{data.teams.some(({ id }) => selectedTeamIds[id]) && (
						<SettingItem
							title="Default team"
							description="Select which team is preselected when creating a new project."
							action={
								<FormSelect
									name="defaultTeam"
									label={null}
									options={data.teams
										.filter(({ id }) => selectedTeamIds[id])
										.map((team) => ({
											label: team.name,
											value: team.id,
										}))}
									selectClassName="dark:bg-gray-700/70"
								/>
							}
							className="pt-4"
						/>
					)}
				</SettingsContainer>
			</div>

			<div className="space-y-4">
				<div className="mt-8 text-sm font-medium">Automation</div>
				<SettingsContainer>
					<SettingItem
						title="Auto-create issues"
						description="Automatically create Linear issues for every new thread."
						action={<FormSwitch name="autoCreateIssuesEnabled" />}
					/>
					<SettingItem
						title="Auto-create customers"
						description="Every new company syncs to Linear as a customer, before any thread is linked."
						action={<FormSwitch name="autoCreateLinearCustomers" />}
						className="pt-4"
					/>
					<SettingItem
						title="Auto-assign to customer owner"
						description="Threads will be assigned to the customer's owner."
						action={<FormSwitch name="autoAssignFromLinearOwner" />}
						className="pt-4"
					/>
					<SettingItem
						title="Reply to customers from Linear"
						description="Adds a comment on linked Linear issues. Replies to that comment are sent to the customer."
						action={<FormSwitch name="postLinearResourceComment" />}
						className="pt-4"
					/>
					<SettingItem
						title="Suggest issues and tags"
						description="Link the right issue and tags in one click."
						action={<FormSwitch name="linkSuggestionsEnabled" />}
						className="pt-4"
					/>
				</SettingsContainer>
			</div>

			<div className="space-y-4">
				<div className="mt-8 text-sm font-medium">Display options</div>
				<SettingsContainer>
					<SettingItem
						title="Revenue formatting"
						description="Choose between displaying monthly or annual MRR per customer."
						action={
							<FormSelect
								name="revenueFormatting"
								label={null}
								options={revenueFormattingOptions}
								selectClassName="dark:bg-gray-700/70"
							/>
						}
					/>
					<SettingItem
						title="Revenue currency"
						description="The currency customer revenue is displayed in."
						action={
							<FormSelect
								name="revenueCurrency"
								label={null}
								options={REVENUE_CURRENCY_OPTIONS}
								selectClassName="dark:bg-gray-700/70"
							/>
						}
						className="pt-4"
					/>
				</SettingsContainer>
			</div>
		</Form>
	)
}
